import styles from './index.module.css'

import cn from 'clsx'
import React, { useLayoutEffect } from 'react'

import type { FilterState } from 'types/filter-page'

import { Box, Flex, Popover } from 'components/ds'

import { filterDataTypes } from '../config'
import FilterBadge from '../filter-badge'
import FilterBadgeTrigger from '../filter-badge/filter-badge-trigger'
import {
  computeFilterChange,
  computeFilterDelete,
  getFilterBadgeKey,
} from '../utils'
import MoreBadgeButton from './more-badge-button'
import { useOverflowRestraint } from './use-overflow-restraint'

type Props = {
  value: FilterState[]
  onChange: (_value: FilterState[]) => void
  // Rendered after the badges and reserved in the width budget, so it stays next to them
  trailing?: React.ReactNode
}

type VisibleFilter = { filterState: FilterState; index: number }

const badgeKey = ({ filterState, index }: VisibleFilter): string =>
  getFilterBadgeKey(filterState, index)

// Looks like FilterBadge but has no popover; used only to measure width
const GhostBadge = ({
  filterState,
}: {
  filterState: FilterState
}): React.ReactNode => {
  const filterDefinition = filterDataTypes.find(
    filter => filter.id === filterState.definition.type,
  )
  if (!filterDefinition) return null

  return (
    <FilterBadgeTrigger
      filterState={filterState}
      filterDefinition={filterDefinition}
      tabIndex={-1}
    />
  )
}

// Shows filter badges in one row; any that don't fit go into a "+N more" popover
const OverflowFilterBadges = ({
  value,
  onChange,
  trailing,
}: Props): React.ReactNode => {
  const handleFilterChange = (index: number, newValue: FilterState): void =>
    onChange(computeFilterChange(value, index, newValue))

  const handleFilterDelete = (index: number): void =>
    onChange(computeFilterDelete(value, index))

  // Keeps original indexes so edits and deletes hit the right filter
  const visibleFilters: VisibleFilter[] = value.flatMap((filterState, index) =>
    filterState.hidden ? [] : [{ filterState, index }],
  )

  // Changes when a filter's operator or value changes, so widths are re-measured
  const filterValuesKey = visibleFilters
    .map(
      ({ filterState, index }) =>
        `${filterState.id ?? index}:${
          filterState.operator ?? ''
        }:${JSON.stringify(filterState.value)}`,
    )
    .join('|')

  const {
    containerRef,
    itemRefs,
    moreBadgeRefs,
    trailingRef,
    visibleCount,
    isResizing,
  } = useOverflowRestraint(
    visibleFilters.length,
    filterValuesKey,
    Boolean(trailing),
  )

  // A new filter awaiting input stays in the main row so its open popover isn't hidden in "+N more"
  const isAwaitingInput = ({ filterState }: VisibleFilter): boolean =>
    !filterState.definition.fixed && filterState.value === undefined

  // That badge isn't in the measured budget, so the row can overflow; scroll it into view
  const awaitingCount = visibleFilters.filter(isAwaitingInput).length
  useLayoutEffect(() => {
    const row = containerRef.current?.parentElement
    if (awaitingCount > 0 && row) row.scrollLeft = row.scrollWidth
  }, [containerRef, awaitingCount])

  const shownFilters = [
    ...visibleFilters.slice(0, visibleCount),
    ...visibleFilters.slice(visibleCount).filter(isAwaitingInput),
  ]
  const overflowFilters = visibleFilters
    .slice(visibleCount)
    .filter(item => !isAwaitingInput(item))

  return (
    <div
      ref={containerRef}
      className={cn(styles.container, { [styles.resizing]: isResizing })}
    >
      {/* Deliberate hidden copy: every badge is rendered so the hook can read its width */}
      <Flex
        className={styles.shadowRow}
        gap="2"
        wrap="nowrap"
        aria-hidden="true"
      >
        {visibleFilters.map((item, i) => (
          <div
            key={badgeKey(item)}
            ref={el => {
              itemRefs.current[i] = el
            }}
          >
            <GhostBadge filterState={item.filterState} />
          </div>
        ))}

        {Array.from(
          { length: String(visibleFilters.length).length },
          (_, i) => (
            <div
              key={i}
              ref={el => {
                moreBadgeRefs.current[i] = el
              }}
            >
              {/* Widest N with i + 1 digits (9, 99, ...) */}
              <MoreBadgeButton count={10 ** (i + 1) - 1} tabIndex={-1} />
            </div>
          ),
        )}
      </Flex>

      <Flex gap="2" wrap="nowrap" align="center" minWidth="0">
        {shownFilters.map(({ filterState, index }) => (
          <FilterBadge
            key={badgeKey({ filterState, index })}
            value={filterState}
            onChange={value => handleFilterChange(index, value)}
            onDelete={() => handleFilterDelete(index)}
          />
        ))}

        {overflowFilters.length > 0 && (
          <Popover.Root modal={false}>
            <Popover.Trigger>
              <MoreBadgeButton count={overflowFilters.length} />
            </Popover.Trigger>
            <Popover.Content className={styles.overflowContent} align="start">
              <Flex direction="column" gap="2">
                {overflowFilters.map(({ filterState, index }) => (
                  <Box key={badgeKey({ filterState, index })}>
                    <FilterBadge
                      value={filterState}
                      onChange={value => handleFilterChange(index, value)}
                      onDelete={() => handleFilterDelete(index)}
                    />
                  </Box>
                ))}
              </Flex>
            </Popover.Content>
          </Popover.Root>
        )}

        {trailing && <div ref={trailingRef}>{trailing}</div>}
      </Flex>
    </div>
  )
}

export default OverflowFilterBadges
